import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RecipeService } from '../../../services/recipe-services/recipe.service';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth';
import {
  CONSTANTS_DETAILS,
  Recipe,
  RecipeCategory,
  RecipeCountry,
  RecipeCreatePayload,
  RecipeRegion,
  PeoplePickerList,
  ProjectListItem,
} from '../../../shared/model';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { forkJoin, take } from 'rxjs';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PeoplePickerComponent } from '../../../shared/components/people-picker/people-picker/people-picker.component';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { NgClass } from '@angular/common';
@Component({
  selector: 'app-new-recipe-dialog',
  standalone: true,
  imports: [
    NgClass,
    MatDialogModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    PeoplePickerComponent,
    MatIconModule,
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatOptionModule,
    MatInputModule,
    FormsModule,
  ],
  templateUrl: './new-recipe-dialog.component.html',
  styleUrl: './new-recipe-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewRecipeDialogComponent implements OnInit {
  @ViewChild('dialogContent') dialogContent!: ElementRef<HTMLDivElement>;
  private authService = inject(AuthService);
  public userProfile = this.authService.getAccountGroups();
  public selectedValue = '';

  constructor(
    public _dialogRef: MatDialogRef<NewRecipeDialogComponent>,
    public _recipeService: RecipeService,
    private _router: Router,
    private _snackbarService: SnackbarService,
    private _translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public _recipeDetails: Recipe | null,
  ) {}

  regions: RecipeRegion[] = [];
  countries: RecipeCountry[] = [];
  categories: RecipeCategory[] = [];

  public filteredProjects = signal<ProjectListItem[]>([]);
  public selectedPeoplesData: string[] = [];
  public showPeoplePicker: boolean = false;
  public hasSearchedProjects = signal(false);

  ngOnInit() {
    forkJoin([
      this._recipeService.getRegion(),
      this._recipeService.getCategory(),
    ])
      .pipe(take(2))
      .subscribe({
        next: (response) => {
          this.regions = (response[0].regions || []).filter(
            (region) => region.isEnabled,
          );
          this.categories = (response[1].categories || []).filter(
            (category) => category.isEnabled,
          );
          const beverageCategory = this.categories.find(
            (category) => category.name?.toLowerCase() === 'beverage',
          );
          if (beverageCategory) {
            const categoryId = beverageCategory.id
              ? beverageCategory.id
              : this._recipeDetails?.categoryId;
            this.controls.category.setValue(categoryId ?? null);
          }
          this.updateRecipeDetailsFromExisting();
        },
        error: () => {
          //Intentionally ignored
        },
      });
    this.controls.country.disable();
    this.form = new FormGroup(this.controls);
  }

  readonly controls = {
    recipeName: new FormControl('', [Validators.required]),
    projectSearch: new FormControl(''),
    region: new FormControl<number | null>(null, [Validators.required]),
    country: new FormControl<number | null>(null, [Validators.required]),
    category: new FormControl<number | null>(null, [Validators.required]),
    isTiedToProject: new FormControl(false),
    isMadePrivate: new FormControl(false),
    showInfo: new FormControl(false),
  };

  public form!: FormGroup;

  public showErrors = false;

  public searchProjects() {
    const searchTerm: string = this.controls.projectSearch.value || '';
    if (searchTerm && searchTerm.trim()) {
      this._recipeService.getProjectList(searchTerm).then((response) => {
        this.filteredProjects.set(response?.projectDetails || []);
        this.hasSearchedProjects.set(true);
      });
    }
  }

  /**
   * Handles the selection of a project from the autocomplete dropdown.
   * Updates the form state with the selected project details.
   * @param event The selected project event from the autocomplete.
   */
  public async onProjectSelected(event: MatAutocompleteSelectedEvent) {
    const projectName: string = event.option.value as string;
    const selectedProject = this.filteredProjects().find(
      (project) => project.name === projectName,
    );
    if (selectedProject) {
      this.controls.projectSearch.setValue(projectName);
    }
  }

  /**
   * Toggles the visibility of additional information in the dialog.
   * This method is called when the user clicks on the "Show Info" button.
   */
  public toggleIsTiedToProject() {
    const tied = !this.controls.isTiedToProject.value;
    this.controls.isTiedToProject.setValue(tied);

    // Disable/enable region and country based on isTiedToProject
    if (tied) {
      this.controls.projectSearch.setValidators([Validators.required]);
    } else {
      this.controls.projectSearch.clearValidators();
      this.controls.projectSearch.setValue('');
    }
    this.controls.projectSearch.updateValueAndValidity();
  }

  /**
   * Toggles the visibility of the "Made Private" option in the dialog.
   * This method is called when the user clicks on the "Make Private" toggle.
   */
  public toggleIsMadePrivate() {
    const current = this.controls.isMadePrivate.value;
    this.controls.isMadePrivate.setValue(!current);
    if (!current) {
      this.showPeoplePicker = false;
    }
  }

  /**
   * Handles the select event for the region field.
   * Updates the region signal with the current selected value.
   * Fetches the countries for the selected region.
   */
  public setRegion(region?: Event) {
    const regionId = Number(region);
    this.controls.region.setValue(regionId);
    if (!regionId) {
      this.controls.country.setValue(null);
      this.controls.country.disable();
      this.countries = [];
      return;
    }
    this.getCountryDetails(regionId);
  }

  markAllControlsAsTouched() {
    Object.values(this.controls).forEach((ctrl) => ctrl.markAsTouched());
  }

  /**
   * Creates a new recipe with the current form state.
   * This method is called when the user submits the form.
   */
  public createRecipe() {
    this.showErrors = true;
    this.markAllControlsAsTouched();
    this.showPeoplePicker = false;
    const selectedPeople =
      this.selectedPeoplesData.length > 0
        ? {
            label: 'selectedPeoples',
            value: this.selectedPeoplesData.join(', '),
          }
        : undefined;

    const recipeName = this.controls.recipeName.value;
    if (!recipeName || recipeName.trim() === '') {
      this._snackbarService.open({
        message: this._translate.instant('nameCannotBeBlank'),
        type: 'error',
      });
      return;
    }

    if (this.form.invalid) {
      this._snackbarService.open({
        message: this._translate.instant('enterAllFields'),
        type: 'error',
      });
      return;
    }
    const payload: RecipeCreatePayload = {
      regionId: this.controls.region.value,
      countryId: this.controls.country.value,
      categoryId: this.controls.category.value,
      currencyId: 1,
      recipeName: this.controls.recipeName.value,
      projectName: this.controls.projectSearch.value,
      isRecipeLinked: this.controls.isTiedToProject.value,
      isPrivate: this.controls.isMadePrivate.value,
      createdBy: this.userProfile?.username ?? null,
      isFinal: false,
      isActive: true,
      recipeAccessors: {
        accessorEmails: selectedPeople?.value
          ? selectedPeople?.value.split(',').map((email) => email.trim())
          : [],
        isReadOnly: false,
      },
    };
    if (this._recipeDetails && Object.keys(this._recipeDetails).length) {
      payload.previousRecipeId = this._recipeDetails.recipeId;
    }
    const createUrl =
      this._recipeDetails && Object.keys(this._recipeDetails).length
        ? CONSTANTS_DETAILS.CREATE_FROM_EXISTING_RECIPES
        : CONSTANTS_DETAILS.CREATE_RECIPE_URL;
    this._recipeService.createRecipe(payload, createUrl).then((response) => {
      const identifier = response.recipeIdentifier
        ? response.recipeIdentifier
        : response.identifier;
      this._dialogRef.close();
      this._router.navigate(['recipe/recipe-details', identifier], {
        state: {
          recipe: {
            recipeIdentifier: response.recipeIdentifier,
          },
        },
      });
    });
  }

  /**
   * Scrolls the dialog content to the bottom.
   *
   * @memberof NewRecipeDialogComponent
   */
  public scrollToBottom() {
    setTimeout(() => {
      if (this.dialogContent?.nativeElement) {
        this.dialogContent.nativeElement.scrollTop =
          this.dialogContent.nativeElement.scrollHeight;
      }
    });
  }

  /**
   * Closes the dialog without saving any changes.
   * This method is called when the user clicks on the "Cancel" button.
   */
  closeDialog() {
    this._dialogRef.close();
  }

  /**
   * Updates the recipe details from an existing recipe.
   *
   * @private
   * @param {Recipe} recipe
   * @memberof NewRecipeDialogComponent
   */
  private updateRecipeDetailsFromExisting() {
    let isTiedToProject: boolean = false;
    if (this._recipeDetails?.projectName) {
      isTiedToProject = true;
      this.controls.projectSearch.setValue(
        this._recipeDetails?.projectName ?? null,
      );
      this.controls.isTiedToProject.setValue(isTiedToProject);
    }
    this.controls.recipeName.setValue(this._recipeDetails?.recipeName ?? null);
    if (this._recipeDetails?.regionId) {
      this.controls.region.setValue(this._recipeDetails?.regionId ?? null);
      this.getCountryDetails(this._recipeDetails?.regionId);
    }
    if (this._recipeDetails?.categoryId) {
      this.controls.category.setValue(this._recipeDetails?.categoryId ?? null);
    }
  }

  /**
   * Fetches the details of countries based on the selected region.
   *
   * @private
   * @param {number} countryId
   * @memberof NewRecipeDialogComponent
   */
  private async getCountryDetails(regionId: number) {
    try {
      const response = await this._recipeService.getCountry(Number(regionId));
      this.countries = (response.countryDetails || []).filter(
        (country) => country.isEnabled,
      );
      this.controls.country.setValue(this._recipeDetails?.countryId ?? null);

      this.controls.country.enable();
    } catch {
      //Intentionally ignored
    }
  }
  /**
   * Handles the selection of people from the PeoplePicker component.
   *
   * @param {PeoplePickerList[]} event
   * @memberof NewRecipeDialogComponent
   */
  public selectedPeoples(event: PeoplePickerList[]) {
    this.selectedPeoplesData = event.map((item) => item.mail);
  }
}
